/** "Not your role" - one component, one sentence, and **never a Try again** (Q-05).
 *
 * A 403 `insufficient_role` is the server saying *this person's role may not do this*. Asking again
 * changes nothing, so a screen that drew it as a failure with a retry (Import, the recommendations:
 * G-P6-02, G-P6-03) offered the one remedy that cannot work, and said "could not be loaded" of a
 * thing that was loaded and refused. It says **who can** instead - design rule 6 arriving where the
 * control was reachable by address.
 *
 * Two sources for the roles, and the second is the server's own:
 *
 * - `action` - a row of `shell/whoCan.ts` (*"An Admin or an Approver adds or changes a vendor."*),
 *   for a screen that knows what the person cannot do and says so where the control would have been.
 * - `needs` - `Refused.needs`, the roles a refused read said it needs (*"Only an Admin, an Approver or
 *   a Contributor can use this; you are a Viewer."*), for a read the server refused.
 *
 * **Display only**: the server stays the authority, and nothing here hides a thing the server would
 * have allowed. The sentence is a fact about access and carries no alarm.
 */

import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { refusedSentence, whoCanSentence, type Action } from "../shell/whoCan";
import styles from "./NotYourRole.module.css";

export function NotYourRole({
  action,
  needs,
  testId = "not-your-role",
  inline = false,
  children,
}: {
  action?: Action;
  needs?: readonly string[];
  testId?: string;
  /** The sentence alone, as a span, to stand first in a paragraph the screen already has: a page that
   * already says what the role does keeps that, and the refusal leads it without a second paragraph. */
  inline?: boolean;
  /** A way on, if there is one (a link to what the role can read instead). */
  children?: ReactNode;
}) {
  const { data: me } = useMe();
  const text = action ? whoCanSentence(action) : refusedSentence(needs ?? [], me?.role);
  if (inline) {
    return (
      <span data-testid={testId}>
        {text}
        {children ? <> {children}</> : null}
      </span>
    );
  }
  return (
    <p className={styles.note} role="note" data-testid={testId}>
      {text}
      {children ? <> {children}</> : null}
    </p>
  );
}
